import { use, useEffect, useMemo, useState } from "react";

import { type AgentLabelSources, entityLabel, isEntityPresent } from "./entityLabel";
import type { AgentEntityRef } from "./runner/types";
import { AllRecordsContext } from "@domain/context/accountingData/RecordsCtx";
import { BankTransactionsContext } from "@domain/context/BanksContext";
import { CashRegisterTransactionsContext } from "@domain/context/CashRegisterContext";
import { ContactsContext } from "@domain/context/ContactsContext";
import { useDmsDocumentsMap } from "@domain/context/DmsDataContexts";
import { useTranslation } from "@domain/intl/useTranslation";

/** How long an absent entity may still arrive (records written on the server reach RTDB late). */
export const LABEL_GRACE_MS = 15_000;

/** The live data detail labels are built from; the chat rows and the download read the same maps. */
export const useAgentLabelSources = (): AgentLabelSources => {
    const documents = useDmsDocumentsMap();
    const { map: records } = use(AllRecordsContext);
    const { transactionMap: bankTransactions } = use(BankTransactionsContext);
    const { transactionMap: kasseTransactions } = use(CashRegisterTransactionsContext);
    const { contactsMap: contacts } = use(ContactsContext);
    return useMemo(
        () => ({ documents, records, bankTransactions, kasseTransactions, contacts }),
        [documents, records, bankTransactions, kasseTransactions, contacts],
    );
};

/** The client's label of a detail row, read from live data at render; see entityLabel for the states. */
export const useAgentEntityLabel = (ref: AgentEntityRef): string => {
    const { t } = useTranslation();
    const records = use(AllRecordsContext);
    const sources = useAgentLabelSources();

    const present = isEntityPresent(ref, sources);
    const [graceOver, setGraceOver] = useState(false);
    useEffect(() => {
        if (present || graceOver) {
            return undefined;
        }
        const id = setTimeout(() => setGraceOver(true), LABEL_GRACE_MS);
        return () => clearTimeout(id);
    }, [present, graceOver]);

    // only records carry a loaded flag; the other sources rely on the grace time
    const waiting = !graceOver || (ref.kind === "record" && !records.isLoaded);
    return entityLabel(ref, sources, t, waiting);
};
